import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { tasksAPI, achievementsAPI, usersAPI, fileAPI } from '../services/api';
import { Task, UserTask, Achievement } from '../types';
import { getErrorMessage } from '../utils/errorUtils';
import PageHeader from '../components/PageHeader';
import DataTable, { Column } from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import Modal from '../components/Modal';
import FileViewer from '../components/FileViewer';
import { extractTaskFileNames } from '../utils/fileUtils';
import { useFileViewer } from '../hooks/useFileViewer';
import TaskFileList from '../components/TaskFileList';
import '../styles/global.css';

const HomeworksCheck: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [pendingTasks, setPendingTasks] = useState<UserTask[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTask, setSelectedTask] = useState<UserTask | null>(null);

  const fileViewer = useFileViewer();

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [tasksData, pendingData, achievementsData, usersData] = await Promise.all([
        tasksAPI.getTasks(),
        tasksAPI.getPendingTasks(),
        achievementsAPI.getAchievements(),
        usersAPI.getUsers(),
      ]);
      setTasks(tasksData);
      setPendingTasks(pendingData);
      setAchievements(achievementsData);
      setUsers(usersData);
    } catch (err: any) {
      setError(getErrorMessage(err) || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const getUserName = (userId: number) => {
    const u = users.find((u) => u.id === userId);
    return u ? u.full_name || u.username : 'Неизвестный';
  };

  const getTaskInfo = (userTask: UserTask) => {
    return tasks.find((t) => t.id === userTask.task_id) || {
      title: `Задание #${userTask.task_id}`,
      description: 'Нет описания',
      achievement_id: 0,
      percentage_value: 0,
    };
  };

  const getAchievementTitle = (achievementId: number) => {
    const a = achievements.find((a) => a.id === achievementId);
    return a ? a.title : 'Неизвестное достижение';
  };

  const handleReviewTask = async (userTaskId: number, approved: boolean, comment?: string) => {
    try {
      await tasksAPI.reviewTask(userTaskId, approved, comment);
      loadData();
    } catch (err: any) {
      setError(getErrorMessage(err) || 'Ошибка проверки');
    }
  };

  // ── Колонки таблицы ─────────────────────────────────────────
  const columns: Column<UserTask>[] = [
    { key: 'id', header: 'ID', width: '55px' },
    {
      key: 'task', header: 'Задание',
      render: (ut) => (
        <strong style={{ color: 'var(--primary-orange)' }}>{getTaskInfo(ut).title}</strong>
      ),
    },
    {
      key: 'user', header: 'Пользователь',
      render: (ut) => <span className="status-badge status-pending">{getUserName(ut.user_id)}</span>,
    },
    {
      key: 'witness', header: 'Коллега',
      render: (ut) => ut.witness_user_id
        ? <span className="status-badge status-approved" style={{ fontSize: '11px' }}>
            ✓ {getUserName(ut.witness_user_id)}
          </span>
        : <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>—</span>,
    },
    {
      key: 'achievement', header: 'Достижение',
      render: (ut) => getAchievementTitle(getTaskInfo(ut).achievement_id),
    },
    {
      key: 'percentage', header: '%',
      render: (ut) => (
        <span className="status-badge status-submitted">+{getTaskInfo(ut).percentage_value}%</span>
      ),
    },
    {
      key: 'actions', header: 'Действия',
      render: (ut) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setSelectedTask(ut)}
          >
            Детальный просмотр
          </button>
          <div className="d-flex gap-2">
            <button
              className="btn btn-success btn-sm"
              style={{ flex: 1 }}
              onClick={() => handleReviewTask(ut.id, true)}
            >
              Одобрить
            </button>
            <button
              className="btn btn-danger btn-sm"
              style={{ flex: 1 }}
              onClick={() => {
                const comment = prompt('Причина отклонения:');
                if (comment) handleReviewTask(ut.id, false, comment);
              }}
            >
              Отклонить
            </button>
          </div>
        </div>
      ),
    },
  ];

  if (currentUser?.role !== 'admin' && currentUser?.role !== 'engineering_manager') {
    return <div className="container"><div className="error">Нет прав доступа</div></div>;
  }

  if (loading) return <div className="loading">Загрузка...</div>;

  return (
    <div className="container" style={{ paddingTop: '1.5rem', paddingBottom: '2rem' }}>
      <PageHeader title="Проверка домашних заданий" />
      {error && <div className="error">{error}</div>}

      <DataTable
        columns={columns}
        data={pendingTasks}
        rowKey={(ut) => ut.id}
        emptyText="Нет заданий на проверке — все работы проверены или ещё не отправлены"
        minWidth="700px"
      />

      {/* ── Детальный просмотр задания ──────────────────────── */}
      <Modal
        isOpen={!!selectedTask}
        onClose={() => setSelectedTask(null)}
        maxWidth="900px"
      >
        {selectedTask && (() => {
          const taskInfo = getTaskInfo(selectedTask);
          const allFiles = extractTaskFileNames(selectedTask);
          return (
            <>
              <h2 className="modal-title">Задание: {taskInfo.title}</h2>
              <p className="modal-subtitle">ID: #{selectedTask.task_id}</p>

              {/* Мета-блок */}
              <div className="section-card" style={{ marginBottom: '1.25rem' }}>
                <div style={{ marginBottom: '0.75rem' }}>
                  <span style={{ fontWeight: 500, color: 'var(--text-muted)' }}>Описание: </span>
                  {taskInfo.description}
                </div>
                <div className="d-flex gap-3" style={{ flexWrap: 'wrap' }}>
                  <div className="section-card" style={{ flex: 1, padding: '0.75rem', minWidth: 140 }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Достижение</div>
                    <strong>{getAchievementTitle(taskInfo.achievement_id)}</strong>
                  </div>
                  <div className="section-card" style={{ flex: 1, padding: '0.75rem', minWidth: 140 }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Исполнитель</div>
                    <strong>{getUserName(selectedTask.user_id)}</strong>
                  </div>
                  <div className="section-card" style={{ flex: 1, padding: '0.75rem', minWidth: 140, textAlign: 'center' }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Ценность</div>
                    <strong style={{ color: 'var(--primary-orange)', fontSize: '1.25rem' }}>
                      +{taskInfo.percentage_value}%
                    </strong>
                  </div>
                </div>
              </div>

              {/* Текст выполнения */}
              {selectedTask.submission_text && (
                <div style={{ marginBottom: '1.25rem' }}>
                  <h3 className="section-card__title" style={{ marginBottom: '0.75rem' }}>Описание выполнения</h3>
                  <div className="notification-modal-message">
                    <p>{selectedTask.submission_text}</p>
                  </div>
                </div>
              )}

              {/* Информация о свидетеле */}
              {selectedTask.witness_user_id && (
                <div className="section-card" style={{ marginBottom: '1.25rem', borderLeft: '3px solid var(--primary-orange)', paddingLeft: '1rem' }}>
                  <h3 className="section-card__title" style={{ marginBottom: '0.75rem' }}>
                    👥 Коллега-участник проекта
                  </h3>
                  <div className="d-flex gap-3" style={{ flexWrap: 'wrap' }}>
                    <div className="section-card" style={{ flex: 1, padding: '0.75rem', minWidth: 140 }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Сотрудник</div>
                      <strong>{getUserName(selectedTask.witness_user_id)}</strong>
                    </div>
                    <div className="section-card" style={{ flex: 1, padding: '0.75rem', minWidth: 140 }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Статус подтверждения</div>
                      <StatusBadge
                        status={selectedTask.witness_status === 'confirmed' ? 'approved' : selectedTask.witness_status === 'rejected' ? 'rejected' : 'pending'}
                        label={selectedTask.witness_status === 'confirmed' ? '✓ Подтвердил участие' : selectedTask.witness_status === 'rejected' ? '✗ Отклонил участие' : 'Ожидает ответа'}
                      />
                    </div>
                    {selectedTask.witness_responded_at && (
                      <div className="section-card" style={{ flex: 1, padding: '0.75rem', minWidth: 140 }}>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Ответил</div>
                        <strong>{new Date(selectedTask.witness_responded_at).toLocaleString('ru')}</strong>
                      </div>
                    )}
                  </div>
                  {selectedTask.witness_comment && (
                    <div style={{ marginTop: '0.75rem', padding: '10px 14px', borderRadius: '6px', background: 'var(--bg-surface)', fontSize: '14px', fontStyle: 'italic' }}>
                      «{selectedTask.witness_comment}»
                    </div>
                  )}
                </div>
              )}

              {/* Файлы */}
              {allFiles.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 className="section-card__title" style={{ marginBottom: '0.75rem' }}>
                    Прикреплённые файлы ({allFiles.length})
                  </h3>
                  <TaskFileList
                    filenames={allFiles}
                    folder="tasks"
                    onOpen={(i) => fileViewer.open(allFiles, i)}
                    onDownload={(fn) => fileAPI.downloadFile(fn)}
                  />
                </div>
              )}

              {/* Кнопки одобрения/отклонения */}
              <div className="d-flex gap-2 justify-content-end">
                <button
                  className="btn btn-success"
                  onClick={() => {
                    handleReviewTask(selectedTask.id, true);
                    setSelectedTask(null);
                  }}
                >
                  Одобрить
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => {
                    const comment = prompt('Причина отклонения:');
                    if (comment) {
                      handleReviewTask(selectedTask.id, false, comment);
                      setSelectedTask(null);
                    }
                  }}
                >
                  Отклонить
                </button>
                <button className="btn btn-secondary" onClick={() => setSelectedTask(null)}>
                  Закрыть
                </button>
              </div>
            </>
          );
        })()}
      </Modal>

      {/* ── Просмотрщик файлов ──────────────────────────────── */}
      {fileViewer.isOpen && (
        <FileViewer
          files={fileViewer.files}
          currentIndex={fileViewer.currentIndex}
          folder="tasks"
          onClose={fileViewer.close}
          onNext={fileViewer.next}
          onPrev={fileViewer.prev}
          onDownload={(filename) => fileAPI.downloadFile(filename)}
        />
      )}
    </div>
  );
};

export default HomeworksCheck;
